/**
 * ExpenseReimbursementsPage — Issue #370.
 *
 * 报销单列表 + 创建 + 上下两联打印预览。
 */
import { useEffect, useMemo, useState } from 'react';
import { useAuth } from '@/hooks/useAuth';
import { FINANCE_PERMISSIONS, hasPermission } from '@/lib/financePermissions';
import {
  useReimbursementActions,
  useReimbursements,
  type CreateReimbursementDto,
  type ReimbursementPrintPreviewDto,
  type ReimbursementSummaryDto,
} from '@/hooks/useFinanceSettlement';

const STATUS_LABELS: Record<string, string> = {
  draft: '草稿',
  pending_department_approval: '待部门审核',
  pending_finance_review: '待财务复核',
  pending_cashier_execution: '待出纳执行',
  executed: '已执行',
  rejected: '已驳回',
  cancelled: '已撤回',
};

const STATUS_COLORS: Record<string, string> = {
  draft: 'bg-gray-100 text-gray-700',
  pending_department_approval: 'bg-sky-100 text-sky-700',
  pending_finance_review: 'bg-indigo-100 text-indigo-700',
  pending_cashier_execution: 'bg-purple-100 text-purple-800',
  executed: 'bg-green-100 text-green-800',
  rejected: 'bg-red-100 text-red-700',
  cancelled: 'bg-gray-200 text-gray-500',
};

function StatusBadge({ status }: { status: string }) {
  return (
    <span
      className={`inline-flex items-center rounded-full px-2 py-0.5 text-xs font-medium ${
        STATUS_COLORS[status] ?? 'bg-gray-100 text-gray-600'
      }`}
    >
      {STATUS_LABELS[status] ?? status}
    </span>
  );
}

function emptyDraft(): CreateReimbursementDto {
  return {
    reimbursementNo: '',
    reimbursementDate: new Date().toISOString().slice(0, 10),
    departmentName: '',
    applicantName: '',
    lines: [{ itemName: '', amount: '' }],
  };
}

function PrintPreviewPanel({
  preview,
  onClose,
}: {
  preview: ReimbursementPrintPreviewDto;
  onClose: () => void;
}) {
  return (
    <div className="fixed inset-0 z-50 flex items-start justify-center overflow-auto bg-black/40 p-4">
      <div className="w-full max-w-4xl space-y-4 rounded-lg bg-white p-6 shadow-xl">
        <div className="flex items-center justify-between">
          <h2 className="text-lg font-semibold">报销单打印预览</h2>
          <button onClick={onClose} className="rounded border px-3 py-1 text-sm">
            关闭
          </button>
        </div>
        {preview.copies.map((copy) => (
          <div
            key={copy.copyType}
            className="relative space-y-3 border-2 border-dashed border-gray-300 p-4"
          >
            <div className="pointer-events-none absolute inset-0 flex items-center justify-center">
              <span className="rotate-[-20deg] select-none text-6xl font-extrabold text-red-300 opacity-30">
                {copy.watermark}
              </span>
            </div>
            <div className="relative">
              <div className="text-center text-xl font-bold">{copy.header.title}</div>
              <div className="text-center text-sm text-gray-500">{copy.header.subtitle}</div>
              <div className="mt-2 grid grid-cols-2 gap-2 text-sm md:grid-cols-4">
                <div>部门：{copy.meta.departmentName}</div>
                <div>日期：{copy.meta.reimbursementDate}</div>
                <div>单号：{copy.meta.reimbursementNo}</div>
                <div>附件张数：{copy.meta.attachmentPages}</div>
              </div>
              {copy.summary ? (
                <div className="mt-2 text-sm">摘要：{copy.summary}</div>
              ) : null}
              <table className="mt-3 w-full text-sm">
                <thead className="bg-gray-50 text-xs">
                  <tr>
                    <th className="border px-2 py-1 text-left">序号</th>
                    <th className="border px-2 py-1 text-left">报销内容</th>
                    <th className="border px-2 py-1 text-left">摘要</th>
                    <th className="border px-2 py-1 text-right">金额</th>
                    <th className="border px-2 py-1 text-left">备注</th>
                  </tr>
                </thead>
                <tbody>
                  {copy.lines.map((line) => (
                    <tr key={line.lineNo}>
                      <td className="border px-2 py-1">{line.lineNo}</td>
                      <td className="border px-2 py-1">{line.itemName}</td>
                      <td className="border px-2 py-1">{line.detail ?? ''}</td>
                      <td className="border px-2 py-1 text-right font-mono">{line.amount}</td>
                      <td className="border px-2 py-1">{line.remarks ?? ''}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
              <div className="mt-2 grid grid-cols-3 gap-2 text-sm">
                <div>合计金额：{copy.totals.totalAmount}</div>
                <div>大写：{copy.totals.totalAmountInWords ?? ''}</div>
                <div>原借支：{copy.totals.originalAdvanceAmount}</div>
                <div>退还：{copy.totals.refundAmount}</div>
                <div>补付：{copy.totals.repayAmount}</div>
              </div>
              <div className="mt-2 grid grid-cols-2 gap-2 text-sm md:grid-cols-5">
                <div>部门审核：{copy.approval.departmentReviewerName ?? '—'}</div>
                <div>财务复核：{copy.approval.financeReviewerName ?? '—'}</div>
                <div>会计：{copy.approval.accountantName ?? '—'}</div>
                <div>出纳：{copy.approval.cashierName ?? '—'}</div>
                <div>报销人：{copy.approval.reimburserName}</div>
              </div>
              {copy.remarks ? (
                <div className="mt-2 text-sm">备注：{copy.remarks}</div>
              ) : null}
              <div className="mt-2 text-xs text-gray-500">状态：{STATUS_LABELS[copy.status] ?? copy.status}</div>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}

export default function ExpenseReimbursementsPage() {
  const { permissions } = useAuth();
  const granted = permissions ?? [];
  const canWrite = hasPermission(
    granted,
    FINANCE_PERMISSIONS.EXPENSE_REIMBURSEMENT_WRITE,
  );
  const canDepartmentApprove = hasPermission(
    granted,
    FINANCE_PERMISSIONS.EXPENSE_REIMBURSEMENT_DEPARTMENT_APPROVE,
  );
  const canFinanceReview = hasPermission(
    granted,
    FINANCE_PERMISSIONS.EXPENSE_REIMBURSEMENT_FINANCE_REVIEW,
  );
  const canCashier = hasPermission(
    granted,
    FINANCE_PERMISSIONS.EXPENSE_REIMBURSEMENT_CASHIER_EXECUTE,
  );
  const canManage = hasPermission(
    granted,
    FINANCE_PERMISSIONS.EXPENSE_REIMBURSEMENT_MANAGE,
  );

  const { data, loading, error, fetchList } = useReimbursements();
  const actions = useReimbursementActions();

  const [statusFilter, setStatusFilter] = useState('');
  const [searchTerm, setSearchTerm] = useState('');
  const [draft, setDraft] = useState<CreateReimbursementDto>(emptyDraft());
  const [creating, setCreating] = useState(false);
  const [preview, setPreview] = useState<ReimbursementPrintPreviewDto | null>(null);
  const [actionMessage, setActionMessage] = useState<string | null>(null);

  useEffect(() => {
    void fetchList({ status: statusFilter || undefined, search: searchTerm || undefined });
  }, [fetchList, statusFilter, searchTerm]);

  const rows = useMemo(() => data?.rows ?? [], [data]);

  const updateLine = (index: number, patch: Partial<{ itemName: string; amount: string; detail: string }>) => {
    const lines = draft.lines.slice();
    lines[index] = { ...lines[index], ...patch };
    setDraft({ ...draft, lines });
  };

  const addLine = () => {
    setDraft({ ...draft, lines: [...draft.lines, { itemName: '', amount: '' }] });
  };

  const removeLine = (index: number) => {
    const lines = draft.lines.slice();
    lines.splice(index, 1);
    setDraft({ ...draft, lines: lines.length === 0 ? [{ itemName: '', amount: '' }] : lines });
  };

  const submitDraft = async () => {
    if (!canWrite) return;
    setActionMessage(null);
    const result = await actions.create(draft);
    if (result) {
      setActionMessage(`已创建报销单：${result.reimbursementNo}`);
      setDraft(emptyDraft());
      setCreating(false);
      await fetchList({ status: statusFilter || undefined });
    }
  };

  const openPreview = async (row: ReimbursementSummaryDto) => {
    const result = await actions.printPreview(row.id);
    if (result) {
      setPreview(result);
    }
  };

  const promptReason = (prefix: string): string | null => {
    const reason = window.prompt(`${prefix}原因（必填）`);
    if (reason === null) return null;
    const trimmed = reason.trim();
    if (trimmed.length === 0) {
      setActionMessage(null);
      return null;
    }
    return trimmed;
  };

  const handleSubmit = async (row: ReimbursementSummaryDto) => {
    setActionMessage(null);
    const result = await actions.submit(row.id, row.recordVersion);
    if (result) {
      setActionMessage(`${result.reimbursementNo} 已提交`);
      await fetchList({ status: statusFilter || undefined });
    }
  };

  const handleDepartmentReview = async (row: ReimbursementSummaryDto) => {
    setActionMessage(null);
    const result = await actions.departmentReview(row.id, {
      expectedRecordVersion: row.recordVersion,
    });
    if (result) {
      setActionMessage(
        `${result.reimbursementNo} 部门审核通过 -> ${
          STATUS_LABELS[result.status] ?? result.status
        }`,
      );
      await fetchList({ status: statusFilter || undefined });
    }
  };

  const handleDepartmentReject = async (row: ReimbursementSummaryDto) => {
    const reason = promptReason('部门审核驳回');
    if (!reason) return;
    setActionMessage(null);
    const result = await actions.departmentReject(row.id, {
      expectedRecordVersion: row.recordVersion,
      reason,
    });
    if (result) {
      setActionMessage(`${result.reimbursementNo} 已被部门驳回`);
      await fetchList({ status: statusFilter || undefined });
    }
  };

  const handleFinanceReview = async (row: ReimbursementSummaryDto) => {
    setActionMessage(null);
    const result = await actions.financeReview(row.id, {
      expectedRecordVersion: row.recordVersion,
    });
    if (result) {
      setActionMessage(
        `${result.reimbursementNo} 财务复核通过 -> ${
          STATUS_LABELS[result.status] ?? result.status
        }`,
      );
      await fetchList({ status: statusFilter || undefined });
    }
  };

  const handleFinanceReject = async (row: ReimbursementSummaryDto) => {
    const reason = promptReason('财务复核驳回');
    if (!reason) return;
    setActionMessage(null);
    const result = await actions.financeReject(row.id, {
      expectedRecordVersion: row.recordVersion,
      reason,
    });
    if (result) {
      setActionMessage(`${result.reimbursementNo} 已被财务驳回`);
      await fetchList({ status: statusFilter || undefined });
    }
  };

  const handleCashier = async (row: ReimbursementSummaryDto) => {
    setActionMessage(null);
    const result = await actions.cashierExecute(row.id, {
      expectedRecordVersion: row.recordVersion,
      paymentMethod: 'bank_transfer',
      executedAt: new Date().toISOString().slice(0, 10),
    });
    if (result) {
      setActionMessage(`${result.reimbursementNo} 出纳已执行`);
      await fetchList({ status: statusFilter || undefined });
    }
  };

  const handleCancel = async (row: ReimbursementSummaryDto) => {
    const reason = promptReason('撤回');
    if (!reason) return;
    setActionMessage(null);
    const result = await actions.cancel(row.id, {
      expectedRecordVersion: row.recordVersion,
      reason,
    });
    if (result) {
      setActionMessage(`${result.reimbursementNo} 已撤回`);
      await fetchList({ status: statusFilter || undefined });
    }
  };

  const handleReturnToDraft = async (row: ReimbursementSummaryDto) => {
    setActionMessage(null);
    const result = await actions.returnToDraft(row.id, row.recordVersion);
    if (result) {
      setActionMessage(`${result.reimbursementNo} 已回到草稿`);
      await fetchList({ status: statusFilter || undefined });
    }
  };

  const canCancel = (row: ReimbursementSummaryDto): boolean => {
    if (!canManage) return false;
    return (
      row.status === 'draft' ||
      row.status === 'pending_department_approval' ||
      row.status === 'pending_finance_review' ||
      row.status === 'pending_cashier_execution'
    );
  };

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <h1 className="text-xl font-semibold">费用报销单</h1>
        {canWrite ? (
          <button
            onClick={() => setCreating((v) => !v)}
            className="rounded-md bg-primary px-3 py-1.5 text-sm text-primary-foreground shadow-sm"
          >
            {creating ? '取消' : '新建'}
          </button>
        ) : null}
      </div>

      <div className="flex flex-wrap gap-2">
        <select
          className="rounded border px-2 py-1 text-sm"
          value={statusFilter}
          onChange={(e) => setStatusFilter(e.target.value)}
        >
          <option value="">全部状态</option>
          {Object.entries(STATUS_LABELS).map(([value, label]) => (
            <option key={value} value={value}>
              {label}
            </option>
          ))}
        </select>
        <input
          type="text"
          className="rounded border px-2 py-1 text-sm"
          placeholder="搜索单号 / 部门 / 报销人"
          value={searchTerm}
          onChange={(e) => setSearchTerm(e.target.value)}
        />
      </div>

      {actionMessage ? (
        <div className="rounded border border-green-200 bg-green-50 px-3 py-2 text-sm text-green-800">
          {actionMessage}
        </div>
      ) : null}
      {error ? (
        <div className="rounded border border-red-200 bg-red-50 px-3 py-2 text-sm text-red-700">
          {error.message}
        </div>
      ) : null}
      {actions.error ? (
        <div className="rounded border border-red-200 bg-red-50 px-3 py-2 text-sm text-red-700">
          {actions.error.message}
        </div>
      ) : null}

      {creating ? (
        <div className="space-y-3 rounded border bg-white p-4 shadow-sm">
          <h2 className="text-base font-semibold">新建报销单</h2>
          <div className="grid grid-cols-1 gap-3 md:grid-cols-2">
            <label className="flex flex-col gap-1 text-sm">
              <span className="text-gray-600">单号</span>
              <input
                type="text"
                className="rounded border px-2 py-1"
                value={draft.reimbursementNo}
                onChange={(e) => setDraft({ ...draft, reimbursementNo: e.target.value })}
              />
            </label>
            <label className="flex flex-col gap-1 text-sm">
              <span className="text-gray-600">日期</span>
              <input
                type="date"
                className="rounded border px-2 py-1"
                value={draft.reimbursementDate}
                onChange={(e) => setDraft({ ...draft, reimbursementDate: e.target.value })}
              />
            </label>
            <label className="flex flex-col gap-1 text-sm">
              <span className="text-gray-600">部门</span>
              <input
                type="text"
                className="rounded border px-2 py-1"
                value={draft.departmentName}
                onChange={(e) => setDraft({ ...draft, departmentName: e.target.value })}
              />
            </label>
            <label className="flex flex-col gap-1 text-sm">
              <span className="text-gray-600">报销人姓名</span>
              <input
                type="text"
                className="rounded border px-2 py-1"
                value={draft.applicantName}
                onChange={(e) => setDraft({ ...draft, applicantName: e.target.value })}
              />
            </label>
            <label className="flex flex-col gap-1 text-sm">
              <span className="text-gray-600">附件张数</span>
              <input
                type="number"
                min={0}
                className="rounded border px-2 py-1"
                value={draft.attachmentPages ?? 0}
                onChange={(e) =>
                  setDraft({ ...draft, attachmentPages: Number(e.target.value) || 0 })
                }
              />
            </label>
            <label className="flex flex-col gap-1 text-sm">
              <span className="text-gray-600">合计大写</span>
              <input
                type="text"
                className="rounded border px-2 py-1"
                value={draft.totalAmountInWords ?? ''}
                onChange={(e) => setDraft({ ...draft, totalAmountInWords: e.target.value || undefined })}
              />
            </label>
            <label className="flex flex-col gap-1 text-sm md:col-span-2">
              <span className="text-gray-600">摘要</span>
              <textarea
                rows={2}
                className="rounded border px-2 py-1"
                value={draft.summary ?? ''}
                onChange={(e) => setDraft({ ...draft, summary: e.target.value || undefined })}
              />
            </label>
            <label className="flex flex-col gap-1 text-sm md:col-span-2">
              <span className="text-gray-600">原借支单 ID（如有，必须为 outstanding 状态）</span>
              <input
                type="text"
                className="rounded border px-2 py-1"
                value={draft.originalAdvanceId ?? ''}
                onChange={(e) => setDraft({ ...draft, originalAdvanceId: e.target.value || undefined })}
              />
            </label>
            <label className="flex flex-col gap-1 text-sm md:col-span-2">
              <span className="text-gray-600">备注</span>
              <textarea
                rows={2}
                className="rounded border px-2 py-1"
                value={draft.remarks ?? ''}
                onChange={(e) => setDraft({ ...draft, remarks: e.target.value || undefined })}
              />
            </label>
          </div>

          <div>
            <div className="mb-2 flex items-center justify-between">
              <h3 className="text-sm font-medium">明细行</h3>
              <button onClick={addLine} className="rounded border px-2 py-1 text-xs">
                + 添加行
              </button>
            </div>
            <table className="w-full text-sm">
              <thead className="bg-gray-50 text-xs">
                <tr>
                  <th className="border px-2 py-1 text-left">报销内容</th>
                  <th className="border px-2 py-1 text-left">摘要</th>
                  <th className="border px-2 py-1 text-right">金额</th>
                  <th className="border px-2 py-1">操作</th>
                </tr>
              </thead>
              <tbody>
                {draft.lines.map((line, index) => (
                  <tr key={index}>
                    <td className="border px-2 py-1">
                      <input
                        className="w-full rounded border px-2 py-1 text-sm"
                        value={line.itemName}
                        onChange={(e) => updateLine(index, { itemName: e.target.value })}
                      />
                    </td>
                    <td className="border px-2 py-1">
                      <input
                        className="w-full rounded border px-2 py-1 text-sm"
                        value={line.detail ?? ''}
                        onChange={(e) => updateLine(index, { detail: e.target.value })}
                      />
                    </td>
                    <td className="border px-2 py-1">
                      <input
                        className="w-full rounded border px-2 py-1 text-right font-mono text-sm"
                        inputMode="decimal"
                        value={line.amount}
                        onChange={(e) => updateLine(index, { amount: e.target.value })}
                      />
                    </td>
                    <td className="border px-2 py-1 text-center">
                      <button
                        onClick={() => removeLine(index)}
                        className="rounded border px-2 py-1 text-xs"
                      >
                        删除
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          <div className="flex justify-end gap-2">
            <button
              onClick={() => {
                setCreating(false);
                setDraft(emptyDraft());
              }}
              className="rounded border px-3 py-1.5 text-sm"
            >
              取消
            </button>
            <button
              onClick={submitDraft}
              disabled={actions.loading}
              className="rounded bg-primary px-3 py-1.5 text-sm text-primary-foreground"
            >
              {actions.loading ? '提交中…' : '保存草稿'}
            </button>
          </div>
        </div>
      ) : null}

      <div className="overflow-x-auto rounded border bg-white shadow-sm">
        <table className="min-w-full text-sm">
          <thead className="bg-gray-50 text-xs uppercase text-gray-600">
            <tr>
              <th className="px-3 py-2 text-left">单号 / 日期</th>
              <th className="px-3 py-2 text-left">部门 / 报销人</th>
              <th className="px-3 py-2 text-right">合计金额</th>
              <th className="px-3 py-2 text-right">退还 / 补付</th>
              <th className="px-3 py-2 text-left">原借支</th>
              <th className="px-3 py-2 text-left">状态</th>
              <th className="px-3 py-2 text-left">操作</th>
            </tr>
          </thead>
          <tbody>
            {loading ? (
              <tr>
                <td className="px-3 py-4 text-center text-gray-500" colSpan={7}>
                  加载中…
                </td>
              </tr>
            ) : rows.length === 0 ? (
              <tr>
                <td className="px-3 py-4 text-center text-gray-500" colSpan={7}>
                  暂无数据
                </td>
              </tr>
            ) : (
              rows.map((row) => (
                <tr key={row.id} className="border-t hover:bg-gray-50">
                  <td className="px-3 py-2">
                    <div className="font-medium">{row.reimbursementNo}</div>
                    <div className="text-xs text-gray-500">{row.reimbursementDate}</div>
                  </td>
                  <td className="px-3 py-2">
                    <div>{row.departmentName}</div>
                    <div className="text-xs text-gray-500">{row.applicantName}</div>
                  </td>
                  <td className="px-3 py-2 text-right font-mono">{row.totalAmount}</td>
                  <td className="px-3 py-2 text-right font-mono">
                    <div>退 {row.refundAmount}</div>
                    <div>补 {row.repayAmount}</div>
                  </td>
                  <td className="px-3 py-2 text-xs text-gray-600">
                    {row.originalAdvanceId ?? '—'}
                  </td>
                  <td className="px-3 py-2">
                    <StatusBadge status={row.status} />
                  </td>
                  <td
                    className="space-x-1 px-3 py-2"
                    data-testid={`reimbursement-row-actions-${row.id}`}
                  >
                    <button
                      data-testid={`reimbursement-action-print-${row.id}`}
                      className="rounded border px-2 py-1 text-xs"
                      onClick={() => openPreview(row)}
                    >
                      打印预览
                    </button>
                    {row.status === 'draft' && canWrite ? (
                      <button
                        data-testid={`reimbursement-action-submit-${row.id}`}
                        className="rounded border px-2 py-1 text-xs"
                        onClick={() => handleSubmit(row)}
                      >
                        提交
                      </button>
                    ) : null}
                    {row.status === 'pending_department_approval' && canDepartmentApprove ? (
                      <button
                        data-testid={`reimbursement-action-department-review-${row.id}`}
                        className="rounded border px-2 py-1 text-xs"
                        onClick={() => handleDepartmentReview(row)}
                      >
                        部门审核
                      </button>
                    ) : null}
                    {row.status === 'pending_department_approval' && canDepartmentApprove ? (
                      <button
                        data-testid={`reimbursement-action-department-reject-${row.id}`}
                        className="rounded border border-red-300 px-2 py-1 text-xs text-red-700"
                        onClick={() => handleDepartmentReject(row)}
                      >
                        部门驳回
                      </button>
                    ) : null}
                    {row.status === 'pending_finance_review' && canFinanceReview ? (
                      <button
                        data-testid={`reimbursement-action-finance-review-${row.id}`}
                        className="rounded border px-2 py-1 text-xs"
                        onClick={() => handleFinanceReview(row)}
                      >
                        财务复核
                      </button>
                    ) : null}
                    {(row.status === 'pending_finance_review' ||
                      row.status === 'pending_cashier_execution') &&
                    canFinanceReview ? (
                      <button
                        data-testid={`reimbursement-action-finance-reject-${row.id}`}
                        className="rounded border border-red-300 px-2 py-1 text-xs text-red-700"
                        onClick={() => handleFinanceReject(row)}
                      >
                        财务驳回
                      </button>
                    ) : null}
                    {row.status === 'pending_cashier_execution' && canCashier ? (
                      <button
                        data-testid={`reimbursement-action-cashier-execute-${row.id}`}
                        className="rounded border px-2 py-1 text-xs"
                        onClick={() => handleCashier(row)}
                      >
                        出纳执行
                      </button>
                    ) : null}
                    {canCancel(row) ? (
                      <button
                        data-testid={`reimbursement-action-cancel-${row.id}`}
                        className="rounded border border-gray-300 px-2 py-1 text-xs text-gray-700"
                        onClick={() => handleCancel(row)}
                      >
                        撤回
                      </button>
                    ) : null}
                    {row.status === 'rejected' && canWrite ? (
                      <button
                        data-testid={`reimbursement-action-return-to-draft-${row.id}`}
                        className="rounded border px-2 py-1 text-xs"
                        onClick={() => handleReturnToDraft(row)}
                      >
                        回到草稿
                      </button>
                    ) : null}
                  </td>
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>

      {preview ? (
        <PrintPreviewPanel preview={preview} onClose={() => setPreview(null)} />
      ) : null}
    </div>
  );
}
